import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Container,
  Typography,
  Button,
  CircularProgress,
  Paper,
  IconButton,
  Chip,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import HourglassTopIcon from "@mui/icons-material/HourglassTop";
import { apiGet, apiPost } from "../utils/apiClient";
import { HEADER_GRADIENT, HEADER_TEXT } from "../App";

export default function JoinTrip() {
  const { code } = useParams();
  const navigate = useNavigate();
  const upperCode = code?.toUpperCase();

  const [trip, setTrip] = useState(null);
  const [memberStatus, setMemberStatus] = useState(null); // null | 'pending' | 'approved'
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    if (!upperCode || !/^[A-Z]{4}$/.test(upperCode)) {
      navigate("/");
      return;
    }
    apiGet(`/api/trips/code/${upperCode}`)
      .then((data) => {
        setTrip(data.trip);
        setMemberStatus(data.status);
        setLoading(false);
      })
      .catch(() => {
        setError("Trip not found. Check the code and try again.");
        setLoading(false);
      });
  }, [upperCode, navigate]);

  const handleJoin = async () => {
    setJoining(true);
    try {
      const res = await apiPost(`/api/trips/code/${upperCode}/apply`);
      setMemberStatus(res.status ?? "pending");
    } catch {
      setError("Failed to send join request. Please try again.");
    } finally {
      setJoining(false);
    }
  };

  return (
    <Box sx={{ minHeight: "100vh" }}>
      {/* Header */}
      <Box sx={{ background: HEADER_GRADIENT, px: 1, py: 1, display: "flex", alignItems: "center", gap: 1 }}>
        <IconButton size="small" onClick={() => navigate("/")} sx={{ color: HEADER_TEXT }}>
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h6" fontWeight={700} color={HEADER_TEXT} sx={{ flex: 1, textAlign: "center" }}>
          Join Trip
        </Typography>
        <Box sx={{ width: 36 }} />
      </Box>

      <Container maxWidth="sm" sx={{ py: 4 }}>
        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 8 }}>
            <CircularProgress sx={{ color: "#A8D8EA" }} />
          </Box>
        )}

        {error && (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            <Typography fontSize="2.5rem" mb={1}>🔍</Typography>
            <Typography fontWeight={700} color="text.primary" mb={1}>{error}</Typography>
            <Button variant="contained" onClick={() => navigate("/")} sx={{ mt: 2 }}>
              Back to my trips
            </Button>
          </Paper>
        )}

        {trip && !loading && !error && (
          <Paper sx={{ p: 4, textAlign: "center" }}>
            {/* Trip info */}
            <Typography fontSize="2.5rem" mb={1}>✈️</Typography>
            <Typography fontWeight={700} fontSize="1.4rem" color="text.primary">
              {trip.name}
            </Typography>
            {trip.description && (
              <Typography color="text.secondary" mt={0.5}>{trip.description}</Typography>
            )}
            <Box sx={{ display: "flex", justifyContent: "center", gap: 1, mt: 1.5, flexWrap: "wrap" }}>
              <Chip label={trip.currency} size="small"
                sx={{ bgcolor: "rgba(252,227,138,0.4)", color: "text.secondary" }} />
              <Chip label={`${trip.member_count} ${Number(trip.member_count) === 1 ? "member" : "members"}`}
                size="small" sx={{ bgcolor: "rgba(168,216,234,0.3)", color: "text.secondary" }} />
            </Box>

            <Box sx={{ mt: 3 }}>
              {memberStatus === "approved" && (
                <>
                  <CheckCircleIcon sx={{ fontSize: 40, color: "#95E1D3", mb: 1 }} />
                  <Typography fontWeight={700} color="text.primary">You're already a member!</Typography>
                  <Button variant="contained" sx={{ mt: 2 }} onClick={() => navigate(`/trips/${trip.id}`)}>
                    Open Trip
                  </Button>
                </>
              )}

              {memberStatus === "pending" && (
                <>
                  <HourglassTopIcon sx={{ fontSize: 40, color: "#FCE38A", mb: 1 }} />
                  <Typography fontWeight={700} color="text.primary">Request pending</Typography>
                  <Typography variant="body2" color="text.secondary" mt={0.5}>
                    The trip owner needs to approve your request.
                  </Typography>
                  <Button variant="outlined" sx={{ mt: 2 }} onClick={() => navigate("/")}>
                    Back to my trips
                  </Button>
                </>
              )}

              {!memberStatus && (
                <>
                  <Typography variant="body2" color="text.secondary" mb={2}>
                    Request access to join this trip. The owner will approve you.
                  </Typography>
                  <Button variant="contained" size="large" fullWidth onClick={handleJoin}
                    disabled={joining}>
                    {joining ? <CircularProgress size={22} /> : "Request to Join"}
                  </Button>
                </>
              )}
            </Box>
          </Paper>
        )}
      </Container>
    </Box>
  );
}
